import { Injectable, NgZone } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import { getTime, differenceInMinutes } from 'date-fns';
import { SplashScreen } from '@capacitor/splash-screen';
import { ConnectionStatus, Network } from '@capacitor/network';

import * as fromOfflineActions from '../store/actions/offline.actions';
import { OfflineDownloadingService } from './offline-downloading.service';
import { PlatformService } from './platform.service';
import { OfflineService } from './offline.service';
import { AuthService } from './auth.service';
import { stopDownloading } from '../store/actions/offline.actions';
import { sendOfflineStoredEvents } from '../store/actions/offline-statuses.actions';
import { loadApplicationSettings } from '../../settings/application-settings/store/application-settings.actions';
import { initUserDataAfterLogin } from '../store/actions/account-settings.actions';
import { refresh } from '../../inbox/inbox-deliveries/store/inbox.actions';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class ConnectionService {
  private isOnlineStatus = true;
  private isLoggined = false;
  private isPaused = false;
  private isListenedConnection = false;
  private networkType: string;

  constructor(
    private platform: Platform,
    private store: Store<any>,
    private offlineService: OfflineService,
    private platformService: PlatformService,
    private authService: AuthService,
    private offlineDownloadService: OfflineDownloadingService,
    private toastService: ToastService,
    private ngZone: NgZone,
  ) {}

  initConnection() {
    this.setLoginState();

    this.platform.ready().then(() => {
      if (this.platformService.isCordova()) {
        this.onPause();
        this.onResume();
        this.store.dispatch(fromOfflineActions.setBasePath());

       // this.store.dispatch(fromOfflineActions.setNetworkType({ networkType: this.networkType }));
        this.listenConnection();
      }
    });
  }

  get isOnline(): boolean {
    if (this.platformService.isCordova()) {
      return this.isOnlineStatus && navigator?.onLine;
    }
    return true;
  }

  private listenConnection(): void {
    if (!this.isListenedConnection) {
      this.isListenedConnection = true;
      // this.listenOnDisconnect();
      // this.listenOnConnect();
      // this.listenOnNetworkTypeChange();

      Network.addListener('networkStatusChange', status => {
        const isOnline = status.connected;
        this.setNetworkType();
        isOnline ? this.onConnect() : this.onDisconect();
      });

      this.setNetworkType()
    }
  }

  private listenOnDisconnect(): void {
    // this.network.onDisconnect().subscribe(() => {
    //   this.onDisconect();
    // });
  }

  private listenOnConnect(): void {
    // this.network.onConnect().subscribe(() => {
    //   this.onConnect();
    // });
  }

  private listenOnNetworkTypeChange(): void {
    // this.store.dispatch(fromOfflineActions.setNetworkType({ networkType: this.networkType }));

    Network.addListener('networkStatusChange', status => {
      console.log('Network status changed', status);
    });

    // this.network.onChange().subscribe(() => {
    //   this.store.dispatch(fromOfflineActions.setNetworkType({ networkType: this.networkType }));
    // });
  }

  private showOfflineModal(): void {
    if (this.isUUID) {
      this.offlineService.showOfflineModal();
    }
  }

  private setLoginState() {
    this.authService
      .isLoggined()
      .pipe(
        distinctUntilChanged(),
        filter((loginState) => loginState !== this.isLoggined),
      )
      .subscribe((loginState: boolean) => {
        if (this.isLogout(loginState)) {
          this.clearAppData();
        } else {
          this.syncAfterLogin();
        }
        this.isLoggined = loginState;
      });
  }

  private isLogout(loginState: boolean) {
    return this.isLoggined && !loginState;
  }

  private clearAppData() {
    this.offlineDownloadService.clearDownlaodingAllList();
    this.store.dispatch(stopDownloading());
    localStorage.setItem('uuid', '');
  }

  private syncActions(): void {
    this.store.dispatch(sendOfflineStoredEvents());
    this.store.dispatch(refresh());
  }

  private syncAfterLogin(): void {
    this.store.dispatch(loadApplicationSettings());
    this.store.dispatch(initUserDataAfterLogin());
  }

  private onPause() {
    this.platform.pause.subscribe(() => {
      this.setLastPuase();
      this.isPaused = true;
      //this.store.dispatch(fromOfflineActions.setOfflineState({ isOffline: false }));
      if (this.isUUID) {
        this.store.dispatch(stopDownloading());
        this.toastService.dismiss();
        SplashScreen.hide();
      }
    });
  }

  private onResume() {
    this.platform.resume.subscribe(() => {
      this.ngZone.run(() => {
        if (this.isUUID) {
          this.store.dispatch(fromOfflineActions.refreshDeliveriesFromStore());
        }

        if (this.isReloadNeeded()) {
          //this.appReload();
        } else {
          navigator.onLine ? this.onConnect() : this.onDisconect();
          this.isPaused = false;
        }
      });
    });
  }

  private onConnect(): void {
    if (!this.isOnlineStatus) {
      const isOffline = !navigator.onLine;
      this.isOnlineStatus = navigator.onLine;
      //this.store.dispatch(fromOfflineActions.setNetworkType({ networkType: this.networkType }));
      this.store.dispatch(fromOfflineActions.setOfflineState({ isOffline }));
      if (this.isUUID && this.isOnlineStatus) {
        this.offlineService.closeOfflineModal();
        this.syncActions();
      }
    }
  }

  private onDisconect(): void {
    if (this.isOnlineStatus) {
      const isOffline = !navigator.onLine;
      this.isOnlineStatus = navigator.onLine;
      this.store.dispatch(fromOfflineActions.setOfflineState({ isOffline }));
      //this.store.dispatch(fromOfflineActions.setNetworkType({ networkType: this.networkType }));
      if (this.isUUID && !this.isOnlineStatus) {
        this.store.dispatch(stopDownloading());
        this.toastService.dismiss();
        SplashScreen.hide();
        this.showOfflineModal();
      }
    }
  }

  private get isUUID() {
    const uuid = localStorage.getItem('uuid');
    return !!uuid;
  }

  private closeModal() {
    return this.offlineService.closeOfflineModal();
  }

  private appReload() {
    this.closeModal().then(() => {
      SplashScreen.show();
      location.reload();
    });
  }

  private setLastPuase() {
    const now = getTime(new Date());

    localStorage.setItem('lastPause', now.toString());
  }

  private isReloadNeeded() {
    const now = getTime(new Date());
    const lastPause = +localStorage.getItem('lastPause') || now;
    const minFromLastPause = differenceInMinutes(now, lastPause);

    return (
      // eslint-disable-next-line operator-linebreak
      (this.platformService.isIosApp() && minFromLastPause >= 5) ||
      (this.isPaused && !navigator.onLine)
    );
  }

  private async setNetworkType() {
    const status: ConnectionStatus = await Network.getStatus()
    this.networkType = status.connectionType;
    const isOffline = !status.connected;

    this.store.dispatch(fromOfflineActions.setNetworkType({ networkType: this.networkType }));
    this.store.dispatch(fromOfflineActions.setOfflineState({ isOffline }));
  }
}
